/**
 * The website builder's API client — spec §I, ADR-0013, ADR-0014.
 *
 * A client per module, not a growing `lib/api.ts` (waves.md §1.1).
 *
 * **The screens are Wave 4D's** (T-4.6, T-4.12, T-4.13). This is the client
 * they will call, shipped with the service in Wave 3C so that the two were
 * written against each other rather than a wave apart.
 *
 * Three things this file deliberately does **not** do:
 *
 * 1. **It does not validate a block.** `app/domain/site.py: validate_block` is
 *    the only validator, and `blockIssue` below sorts what the server said onto
 *    the block that caused it. Two validators that agree today are two
 *    validators that disagree in a year, and on this module the one the firm
 *    sees would be the one letting a script through.
 * 2. **It does not describe the block forms.** `vocabulary()` returns the nine
 *    schemas, field by field, so the editor draws its controls from the same
 *    description the validator enforces.
 * 3. **It does not take the preview link apart.** The service returns a `path`
 *    and this file adds the origin; nothing here ever reads the token out of
 *    it. The firm holds an address, not a secret — C6, ADR-0024 §2.
 */

import { api } from '../api'

// -- shapes ------------------------------------------------------------------

/**
 * The section library — **twenty-two** (FR-I14, ADR-0022 §2).
 *
 * Re-exported from `site/types.ts` rather than written out a second time.
 * That file is the web half's list and `tests/site-vocabulary.test.ts` already
 * pins it, in order, against the service's own `BLOCK_TYPES` through the
 * generated fixture — so the SPA, the renderer and the validator are one list
 * with one test over it. A union typed out here would have been a third copy
 * of the thing this module's rule 2 exists to prevent, and it was one: it said
 * nine on the day the service stored twenty-two.
 *
 * It is a type-only re-export, so nothing under `site/` is pulled into the
 * staff bundle.
 */
export type { BlockType } from '../../../site/types'
import type { BlockType } from '../../../site/types'

export type PageKind = 'standard' | 'service' | 'custom'
export type VersionStatus = 'draft' | 'published' | 'archived'
export type PostStatus = 'draft' | 'published' | 'archived'
export type DraftStatus = 'offered' | 'accepted' | 'rejected'
export type DraftSource = 'ai' | 'placeholder'

/** The four answers a shipped policy default can rest on (ADR-0014 §2). */
export type PolicyBasis = 'prohibited' | 'conditional' | 'not_addressed' | 'permitted'

/** FR-I11. Every key is present once resolved; `theme` holds only what the
 *  firm actually chose, so "reset" is a meaningful action. */
export interface Theme {
  primary: string
  accent: string
  font: string
  /** C24 item 2 (ADR-0046 §1): seven, from three. The service's
   *  `HEADER_LAYOUTS`, which `site/theme.ts` mirrors and the vocabulary test
   *  holds all three to each other. */
  header_layout: 'split' | 'centred' | 'simple' | 'stacked' | 'inline' | 'bar' | 'minimal'
  /** C24 item 2: five, from two. */
  footer_layout: 'compact' | 'columns' | 'wide' | 'centred' | 'rows'
  /** C17, ADR-0036 §6 — a heading face from `display_choices`, honoured only
   *  by a template that offers a choice (`Template.displayChoices`). */
  display?: string
  /** C17, ADR-0036 §7.3 — `pages` (the default, and every site before C17)
   *  or `sections`: the header built from the home page's anchored sections. */
  nav_mode?: 'pages' | 'sections'
}

export interface Site {
  id: string
  name: string | null
  tagline: string | null
  template: string
  /** Only the firm's own choices. */
  theme: Partial<Theme>
  /** The firm's choices over the template's defaults — what the site looks
   *  like, and what the preview pane must match. */
  resolved_theme: Theme
  logo_url: string | null
  favicon_url: string | null
  contact: Record<string, string>
  social: Record<string, string>
  profession: string | null
}

export interface SiteVersion {
  id: string
  version: number
  status: VersionStatus
  published_at: string | null
  notes: string | null
}

/**
 * The firm's one standing preview link — C6, [ADR-0024](../../../../../docs/adr/0024-the-preview-link-belongs-to-the-site-and-it-is-always-there.md).
 *
 * It belongs to the **site**, not to a version, which is what makes it survive
 * a publish and always show the current draft. `path` is `/_preview/<token>` on
 * the platform host and the origin is this bundle's own — `previewUrl` adds it.
 *
 * `state` is the service's, named rather than inferred from which fields are
 * null, because the sentence the screen shows is different in each:
 *
 * * `live` — there is a link. `path` is set.
 * * `revoked` — the firm turned it off. It stays off until the firm asks for
 *   another; no screen load revives it.
 * * `lapsed` — nobody opened it and nobody edited behind it for the whole
 *   window. Replaced in one press, never silently re-armed.
 * * `none` — there is nothing to preview yet.
 */
export interface PreviewLink {
  state: 'live' | 'revoked' | 'lapsed' | 'none'
  path: string | null
  issued_at: string | null
  expires_at: string | null
  last_seen_at: string | null
  /** What the link would render right now: the firm's draft, or the site it
   *  published and has not touched since. */
  showing: 'draft' | 'published'
}

export interface Block {
  id: string
  type: BlockType
  props: Record<string, unknown>
  display_order: number
}

export interface Page {
  id: string
  site_version_id: string
  slug: string
  title: string
  kind: PageKind
  ref: string | null
  /** The path the site serves this page at. `home` is the root, and the server
   *  says so rather than the browser inferring it — both halves must agree and
   *  only one of them can be tested from the service. */
  href: string
  meta_title: string | null
  meta_description: string | null
  nav_label: string | null
  in_nav: boolean
  display_order: number
  blocks?: Block[]
}

/** **One blog topic** — C24 item 15, ADR-0050 §1. The firm's own, not a
 *  vocabulary: it may rename, reorder, extend or empty the list. */
export interface PostTopic {
  id: string
  name: string
  /** The public filter's value: `/blog?topic=<slug>`. A rename moves it. */
  slug: string
  sort: number
}

export interface Post {
  id: string
  slug: string
  title: string
  summary: string | null
  body: RichText
  author: string | null
  cover_image_url: string | null
  published_on: string | null
  status: PostStatus
  meta_title: string | null
  meta_description: string | null
  href: string
  /** C24 item 15 — **absent** where the caller did not read them, so a screen
   *  can tell *no topics* from *not asked* (ADR-0050 §1). */
  topics?: PostTopic[]
}

export interface AiDraft {
  id: string
  page_id: string | null
  block_type: BlockType
  props: Record<string, unknown>
  alternatives: Record<string, unknown>[]
  /** `placeholder` is the template's own words — which is what every firm gets
   *  today, because the platform's AI service does not exist (spec §9-4). The
   *  screen must not say "we drafted this for you" over it. */
  source: DraftSource
  status: DraftStatus
  accepted_at: string | null
}

// -- rich text ---------------------------------------------------------------
//
// Typed nodes with spans, never a markup string (ADR-0013 §4). There is nothing
// to sanitise because there is nowhere for markup to be — which is the whole
// reason the shape looks like this rather than like HTML.

export type RichNodeKind = 'p' | 'h2' | 'h3' | 'quote' | 'ul' | 'ol'

export interface RichSpan {
  text: string
  strong?: boolean
  em?: boolean
  href?: string
}

export interface RichNode {
  kind: RichNodeKind
  spans?: RichSpan[]
  items?: { spans: RichSpan[] }[]
}

export interface RichText {
  nodes: RichNode[]
}

/** An empty document. `{}` and `{nodes: []}` are the same thing to the server;
 *  this is the one the editor should start from. */
export const EMPTY_RICH_TEXT: RichText = { nodes: [] }

/** Plain text out of a document, for a summary or a character count. Never for
 *  rendering — the public pages are server-rendered (ADR-0013 §2). */
export function richTextToPlain(body: RichText | null | undefined): string {
  if (!body?.nodes) return ''
  const fromSpans = (spans: RichSpan[] | undefined) => (spans ?? []).map((s) => s.text).join('')
  return body.nodes
    .map((node) =>
      node.kind === 'ul' || node.kind === 'ol'
        ? (node.items ?? []).map((item) => fromSpans(item.spans)).join(' ')
        : fromSpans(node.spans),
    )
    .join('\n')
    .trim()
}

// -- the block schemas the editor draws its forms from -----------------------

export interface FieldSchema {
  name: string
  kind:
    | 'text'
    | 'long_text'
    | 'rich'
    | 'url'
    | 'image'
    | 'bool'
    | 'int'
    | 'enum'
    | 'slugs'
    | 'items'
    /** C17, ADR-0036 §2 — an instant, ISO 8601 **with its offset**. */
    | 'datetime'
    /** C20, ADR-0041 §1 (I-3) — up to `max_items` `{label, link}` buttons. */
    | 'buttons'
    /** C23, ADR-0045 §3 — a key of the icon set, or absent for *no icon*. */
    | 'icon'
  label: string
  required?: boolean
  max_length?: number
  choices?: string[]
  of?: FieldSchema[]
  max_items?: number
  /** C21 (ADR-0043 §2, 21A) — the fewest rows an `items` list saves with
   *  (`tabs` 2, `marquee` 3, `comparison.columns` 2). A bound, not a kind. */
  min_items?: number
  /** `buttons` only: the longest a button's label may be. */
  label_max?: number
  /** `buttons` only: the link kinds the picker offers. */
  link_kinds?: ButtonLinkKind[]
  minimum?: number
  maximum?: number
  default?: unknown
}

/**
 * C20, ADR-0041 §1 — a section button **as stored** (waves.md Wave 20 I-3).
 * The render payload carries the resolved `{label, href}` instead.
 */
export type ButtonLinkKind = 'page' | 'booking' | 'login' | 'external'

export type ButtonLink =
  | { kind: 'page'; page_id: string }
  | { kind: 'booking'; flow_slug: string; plan_id?: string }
  | { kind: 'login' }
  | { kind: 'external'; href: string }

export interface SectionButton {
  label: string
  link: ButtonLink | null
}

export interface BlockSchema {
  type: BlockType
  label: string
  description: string
  fields: FieldSchema[]
}

export interface Template {
  code: string
  name: string
  /** The one line the gallery card carries under the name (FR-I10). */
  trait: string
  /** The profession it was drawn for, or `null` for the two neutral ones. It
   *  **orders** the gallery; it never filters it. */
  profession: string | null
  /** C21 (ADR-0043 §7) — the gallery's family chip, one of the vocabulary's
   *  `families`. Optional so a payload from before C21 still types. */
  family?: string
  /** C21 (ADR-0043 §6.1) — the body faces this theme's panel offers; absent
   *  means all of `fonts`. */
  body_choices?: string[]
  theme: Theme
}

/** One shipped policy default, with the citation ADR-0014 §2 requires the admin
 *  to see at the moment they override it. */
export interface PolicyDefault {
  value: boolean
  basis: PolicyBasis
  clause: string | null
  /** The institute's own words. Present on every `prohibited` entry. */
  quote: string | null
  note: string
  source: PolicySource
}

export interface PolicySource {
  id: string
  title: string
  carried_in?: string
  issued_under?: string
  effective: string
  url: string
  announcement_url?: string
  /** `primary` was read from the institute's own document; `secondary` was
   *  not, and `note` says why. The screen should say which. */
  kind: 'primary' | 'secondary'
  read_on: string
  note: string
}

/** C23, ADR-0045 §3, §7 — one icon a firm may pick; the key is ours. */
export interface IconChoice {
  key: string
  label: string
  group: string
}

export interface IconGroup {
  key: string
  label: string
}

/**
 * C23 (waves.md Wave 25 I-1) — the curated set, in the picker's order, as both
 * vocabularies serve it. **Never restated in this bundle**: `app/data/icons.json`
 * is the one source of truth.
 */
export interface IconSet {
  groups: IconGroup[]
  icons: IconChoice[]
}

export interface Vocabulary {
  /** C23: optional so a payload from before C23 still types. */
  icons?: IconSet
  blocks: BlockSchema[]
  block_types: BlockType[]
  page_kinds: PageKind[]
  standard_pages: string[]
  /** Refused as a page address — the web process routes these before the site
   *  renderer ever sees them (ADR-0013 §4). */
  reserved_slugs: string[]
  rich_text_nodes: RichNodeKind[]
  fonts: string[]
  header_layouts: string[]
  footer_layouts: string[]
  /** C17 (18B): the heading faces a template may offer — the service's
   *  `DISPLAY_CHOICES`. Optional so a payload from before C17 still types. */
  display_choices?: string[]
  /** C17 (18B): `pages` and `sections` — FR-I18's *One page / Pages*. */
  nav_modes?: string[]
  /** C21 (ADR-0043 §7): the gallery's ten families, in chip order. */
  families?: string[]
  templates: Template[]
  professions: string[]
  policy: {
    switches: string[]
    available: boolean
    regulator: string | null
    read_on: string | null
    defaults: Record<string, PolicyDefault>
    sources: PolicySource[]
    disclaimers: { required: boolean; text: string; clause: string | null }[]
    disclaimers_note: string
    /** Section type → the switch that suppresses it entirely (ADR-0022 §6).
     *  The service's map, served rather than restated here, so the builder's
     *  "hidden by your policy" mark cannot disagree with the render that
     *  actually drops the section. */
    suppressed_by: Record<string, string>
  }
  preview_days: number
}

/** A sample firm's site, for a gallery card's live demo (FR-I10). The shape is
 *  the render payload, so the demo and the live site go through the same
 *  templates — a demo drawn by different code stops being true. */
export interface Sample {
  sample: true
  profession: string
  note: string
  version: 'sample'
  site: Record<string, unknown>
  policy: { show_pricing: boolean; disclaimers: string[]; switches: Record<string, boolean> }
  services: { slug: string; name: string; description: string | null }[]
  nav: { slug: string; title: string }[]
  pages: { slug: string; title: string; meta: Record<string, string>; blocks: unknown[] }[]
  portal_entry: string
}

// -- the client --------------------------------------------------------------

export const site = {
  vocabulary: () => api<Vocabulary>('/api/v1/site/vocabulary'),
  templates: () =>
    api<{ templates: Template[]; profession: string | null }>('/api/v1/site/templates'),
  sample: (profession: string) => api<Sample>(`/api/v1/site/samples/${profession}`),

  /**
   * The builder's opening read — and **the place the preview link is made**.
   *
   * C6, ADR-0024 §4: a firm never presses anything to get a link, so the read
   * every screen already does is where one appears and where its window is
   * renewed. `preview` is therefore on every response and carries the same
   * address every time.
   */
  read: () =>
    api<{
      site: Site
      draft: SiteVersion | null
      published: SiteVersion | null
      is_live: boolean
      preview: PreviewLink
    }>('/api/v1/site'),
  update: (body: {
    name?: string
    tagline?: string
    template?: string
    theme?: Partial<Theme>
    logo_url?: string
    favicon_url?: string
    contact?: Record<string, string>
    social?: Record<string, string>
  }) => api<Site>('/api/v1/site', { method: 'PATCH', body }),

  versions: () => api<SiteVersion[]>('/api/v1/site/versions'),
  /**
   * Promote the draft (FR-I9). **Behind `site:can_update` and step-up** —
   * T-4.6, and the gate 3C marked as a seam and left off.
   *
   * `assertion` is a fresh re-verification from `core-mfa`. There is no
   * environment in this repo that can mint one, so the refusal a screen sees
   * today is `STEPUP_REQUIRED` wherever `PRODESK_STEPUP_REQUIRED` is on — and
   * the screen acts on the *code*, not on the message, because the two codes
   * mean different things to a person: `STEPUP_REQUIRED` is *ask them to
   * re-verify* and `STEPUP_INVALID` is *that did not work*. Responding to the
   * second by launching the challenge again is a loop.
   */
  /**
   * **Build my website again** — C24 item 10, ADR-0048 §3.
   *
   * Writes a whole site into the **draft** from what the firm has told
   * ProDesk. The live site is untouched; publishing stays the firm's act.
   * Answers the slugs it wrote.
   */
  build: () => api<{ pages: string[] }>('/api/v1/site/build', { method: 'POST', body: {} }),

  publish: (notes?: string, assertion?: string) =>
    api<SiteVersion>('/api/v1/site/publish', {
      method: 'POST',
      body: { notes },
      stepUpAssertion: assertion,
    }),

  /**
   * **Replace** the preview link — it is not how one is created.
   *
   * A firm has a link from its first `read()` (C6, ADR-0024 §4), so this is
   * for the two moments it wants a *different* one: it sent the address to the
   * wrong person, or it turned the link off and changed its mind. The old
   * address stops answering in the same write, and no token comes back — the
   * new `path` does, exactly as `read()` returns it.
   */
  replacePreview: () => api<PreviewLink>('/api/v1/site/preview', { method: 'POST' }),
  /**
   * Withdraw the link, and **the withdrawal sticks**: the service records it,
   * so the next `read()` does not quietly mint a replacement. Getting it back
   * is `replacePreview`, which is a decision rather than a page load.
   */
  revokePreview: () =>
    api<{ revoked: boolean; preview: PreviewLink }>('/api/v1/site/preview', { method: 'DELETE' }),

  pages: (version: 'draft' | 'published' = 'draft') =>
    api<Page[]>(`/api/v1/site/pages?version=${version}`),
  page: (id: string) => api<Page>(`/api/v1/site/pages/${id}`),
  /** FR-I2's standard set plus one page per site-visible service. Idempotent by
   *  slug, because onboarding is resumable and a second run must not double
   *  every page. */
  createStandardPages: () => api<Page[]>('/api/v1/site/pages/standard', { method: 'POST' }),
  createPage: (body: {
    title: string
    slug?: string
    kind?: PageKind
    ref?: string
    nav_label?: string
    in_nav?: boolean
  }) => api<Page>('/api/v1/site/pages', { method: 'POST', body }),
  updatePage: (id: string, body: Partial<Page>) =>
    api<Page>(`/api/v1/site/pages/${id}`, { method: 'PATCH', body }),
  deletePage: (id: string) =>
    api<{ deleted: boolean; slug: string }>(`/api/v1/site/pages/${id}`, { method: 'DELETE' }),

  /** The **whole** page, in order. The editor's unit of work is the page: a
   *  firm reorders three blocks and deletes one in one drag, and a patch
   *  protocol would make the browser responsible for describing that
   *  correctly. */
  saveBlocks: (pageId: string, blocks: { type: BlockType; props: Record<string, unknown> }[]) =>
    api<Page>(`/api/v1/site/pages/${pageId}/blocks`, { method: 'PUT', body: { blocks } }),

  /**
   * **The firm's blog topics** — C24 item 15, ADR-0050 §1.
   *
   * This read is also where a new site is **seeded** with a starter list for
   * its profession, so a firm that has never opened the blog screen still has
   * somewhere to file a post.
   */
  topics: () => api<PostTopic[]>('/api/v1/site/topics'),
  createTopic: (name: string) =>
    api<PostTopic>('/api/v1/site/topics', { method: 'POST', body: { name } }),
  updateTopic: (id: string, body: { name?: string; sort?: number }) =>
    api<PostTopic>(`/api/v1/site/topics/${id}`, { method: 'PATCH', body }),
  /** **Unlinks and keeps every post** (ADR-0050 §1). */
  deleteTopic: (id: string) =>
    api<null>(`/api/v1/site/topics/${id}`, { method: 'DELETE' }),

  posts: (status?: PostStatus) =>
    api<Post[]>(`/api/v1/site/posts${status ? `?status=${status}` : ''}`),
  createPost: (body: Partial<Post> & { title: string; topic_ids?: string[] }) =>
    api<Post>('/api/v1/site/posts', { method: 'POST', body }),
  updatePost: (id: string, body: Partial<Post> & { topic_ids?: string[] }) =>
    api<Post>(`/api/v1/site/posts/${id}`, { method: 'PATCH', body }),
  // w14d — C13, spec §10.5 item 6: a post opens on its own page, and can be
  // deleted (the service archives it; T-4.65).
  post: (id: string) => api<Post>(`/api/v1/site/posts/${id}`),
  deletePost: (id: string) =>
    api<{ deleted: boolean; id: string; slug: string }>(`/api/v1/site/posts/${id}`, { method: 'DELETE' }),

  drafts: (status: DraftStatus = 'offered') => api<AiDraft[]>(`/api/v1/site/drafts?status=${status}`),
  offerDraft: (body: {
    block_type: BlockType
    props: Record<string, unknown>
    alternatives?: Record<string, unknown>[]
    page_id?: string
    source?: DraftSource
  }) => api<AiDraft>('/api/v1/site/drafts', { method: 'POST', body }),
  settleDraft: (id: string, status: 'accepted' | 'rejected') =>
    api<AiDraft>(`/api/v1/site/drafts/${id}/settle`, { method: 'POST', body: { status } }),

  /**
   * T-4.7 — draft the firm's opening copy (FR-I3, **gated on spec §9-4**).
   *
   * `meta.ai_available` is the one thing the screen must read. **It is false
   * today, every time**, because the platform's AI service does not exist —
   * and a screen that said "we drafted this for you" over the template's own
   * placeholder copy would be lying to a firm about its own website. The two
   * cases get two different sentences.
   *
   * An empty `blockTypes` means "the ones worth drafting", which is what
   * onboarding sends.
   */
  generateDrafts: (blockTypes: BlockType[] = [], pageId?: string) =>
    api<AiDraft[]>('/api/v1/site/drafts/generate', {
      method: 'POST',
      body: { block_types: blockTypes, page_id: pageId },
    }),

  /**
   * FR-I15 (C11, Wave 12C) — the Navigation page's one read: the profession
   * line and both menus **of the draft** (they go live on publish), the draft's
   * pages to choose from and the limits the server holds a save to.
   */
  navigation: () => api<Navigation>('/api/v1/site/navigation'),
  /**
   * Save it. **Partial** — a key left out is left alone — and `null` for a
   * menu is the reset to today's (every page marked *in the menu*, and today's
   * footer), which is a value and not an absence.
   */
  saveNavigation: (body: NavigationSave) =>
    api<Navigation>('/api/v1/site/navigation', { method: 'PUT', body }),
}

/* ── FR-I15: the profession line and the menus (C11, Wave 12C) ─────────── */

/**
 * A page link, an external link or — in the header only (C17, ADR-0036 §7.2)
 * — a section on a page: the unit every menu is built from.
 */
export type MenuLink =
  | { kind: 'page'; page_id: string; label?: string | null; icon?: string | null }
  | { kind: 'link'; label: string; url: string; new_tab?: boolean; icon?: string | null }
  | { kind: 'section'; page_id: string; anchor: string; label: string; icon?: string | null }

/**
 * C23 (ADR-0045 §4): `icon` is the firm's pick, stored **only when picked** —
 * on the header's items, its dropdowns and their links. The service drops it
 * from the button (`cta`) and never accepts it in the footer.
 */
export type MenuItem = MenuLink | { kind: 'dropdown'; label: string; items: MenuLink[]; icon?: string | null }

/**
 * C18 (spec §10.10) — one surface's client-portal entry, as stored. A label of
 * `null` is the default ("Login", "Register").
 */
export interface PortalSetting {
  login: { show: boolean; label: string | null }
  register: { show: boolean; label: string | null }
}

/** C18's defaults: *Login* shown, *Register* hidden. */
export const PORTAL_SETTING_DEFAULTS: PortalSetting = {
  login: { show: true, label: null },
  register: { show: false, label: null },
}

/** C18: the most a portal label may hold — the service's `PORTAL_LABEL_MAX`. */
export const PORTAL_LABEL_MAX = 30

export interface HeaderMenu {
  items: MenuItem[]
  cta: MenuLink | null
  portal?: PortalSetting
}

/** C18: a stored header or footer that changes only the portal entry — today's
 *  automatic header, or today's footer, with the firm's entry. */
export interface PortalOnly {
  portal: PortalSetting
}

export type FooterBlock = 'address' | 'hours' | 'contact' | 'social' | 'map' | 'client_login'

export interface FooterMenu {
  columns: { title: string | null; links: MenuLink[] }[]
  blocks: Record<FooterBlock, boolean>
  portal?: PortalSetting
  /** C24 item 14 (ADR-0046 §3) — where each element sits and how loud.
   *  `null` or absent is the footer as it has always been drawn, and the
   *  renderer takes a different path for it entirely. */
  arrangement?: FooterArrangementItem[] | null
}

/** One arranged footer element. The list's order is the order within a slot;
 *  there is deliberately no `order` field (ADR-0046 §3). */
export interface FooterArrangementItem {
  key: string
  /** 1–4, or **0 for not shown**. */
  slot: number
  size: 'small' | 'normal' | 'large'
}

export interface NavigationPage {
  id: string
  slug: string
  title: string
  nav_label: string | null
  in_nav: boolean
  href: string
}

export interface NavigationLimits {
  top_items: number
  dropdown_items: number
  footer_columns: number
  footer_links: number
  label: number
  cta_label: number
  profession_line: number
}

export interface Navigation {
  /** What the firm typed, or `null` — the profession's default. */
  profession_line: string | null
  show_profession_line: boolean
  profession_default: string | null
  /** What the site draws now — edited, else default, else `null` when hidden. */
  profession_line_resolved: string | null
  /** Whether the draft's line or menus differ from the live site's. They go
   *  live only when the draft is published (the owner, 2026-09-25). */
  differs_from_live: boolean
  header_menu: HeaderMenu | PortalOnly | null
  footer_menu: FooterMenu | PortalOnly | null
  pages: NavigationPage[]
  limits: NavigationLimits
  schemes: string[]
  footer_blocks: FooterBlock[]
  footer_block_defaults: Record<FooterBlock, boolean>
  /** C24 item 14 (ADR-0046 §3) — what the footer editor may arrange, and
   *  the three words a size may be. The service's lists, so the editor keeps
   *  no second copy of them. */
  footer_arrangement_keys: string[]
  footer_sizes: string[]
  footer_max_slot: number
  /** C24 item 13 — the layouts this screen **draws and does not set**
   *  (ADR-0046 §4). Resolved, so the diagram shows what a visitor sees. */
  header_layout: string
  footer_layout: string
}

export interface NavigationSave {
  profession_line?: string | null
  show_profession_line?: boolean
  header_menu?: HeaderMenu | PortalOnly | null
  footer_menu?: FooterMenu | PortalOnly | null
}

/** The schemes an external menu link may use — the server's list, restated
 *  so the builder can say so *before* the save (T-4.60). */
export const MENU_SCHEMES = ['https://', 'http://', 'mailto:', 'tel:'] as const

/** Why an external address will be refused, in the firm's words — or `null`. */
export function menuUrlProblem(url: string): string | null {
  const value = url.trim()
  if (!value) return 'Give the link an address.'
  const lowered = value.toLowerCase()
  if (!MENU_SCHEMES.some((scheme) => lowered.startsWith(scheme)) || MENU_SCHEMES.includes(lowered as never)) {
    return value.startsWith('/')
      ? 'For a page on your own site, add it as a page instead.'
      : 'Start the address with https://, http://, mailto: or tel:.'
  }
  return null
}

/**
 * Every limit a menu breaks, as sentences — empty when it would save. The
 * server is the authority and refuses the same things; this says so first.
 */
export function menuProblems(
  header: HeaderMenu | null,
  footer: FooterMenu | null,
  limits: NavigationLimits,
): string[] {
  const problems: string[] = []
  const linkProblem = (link: MenuLink, where: string) => {
    if (link.kind === 'section') {
      // C17: a section has no title of its own to fall back on (18B §2.3).
      if (!link.label.trim()) problems.push(`${where} needs a label.`)
      if (!link.anchor.trim()) problems.push(`${where}: choose a section on the page.`)
    }
    if (link.kind === 'link') {
      if (!link.label.trim()) problems.push(`${where} needs a label.`)
      const url = menuUrlProblem(link.url)
      if (url) problems.push(`${where}: ${url}`)
    }
  }
  if (header) {
    if (header.items.length > limits.top_items) {
      problems.push(`The header holds at most ${limits.top_items} items.`)
    }
    header.items.forEach((item, i) => {
      if (item.kind === 'dropdown') {
        if (!item.label.trim()) problems.push(`Dropdown ${i + 1} needs a label.`)
        if (item.items.length > limits.dropdown_items) {
          problems.push(`“${item.label || 'A dropdown'}” holds at most ${limits.dropdown_items} links.`)
        }
        if (item.items.length === 0) problems.push(`“${item.label || 'A dropdown'}” has no links yet.`)
        item.items.forEach((child, j) => linkProblem(child, `Link ${j + 1} in “${item.label}”`))
      } else {
        linkProblem(item, `Header item ${i + 1}`)
      }
    })
    if (header.cta) {
      if (!(header.cta.label ?? '').trim()) problems.push('The button needs a label.')
      linkProblem(header.cta, 'The button')
    }
  }
  if (footer) {
    if (footer.columns.length > limits.footer_columns) {
      problems.push(`The footer holds at most ${limits.footer_columns} columns.`)
    }
    footer.columns.forEach((column, i) => {
      const name = column.title?.trim() || `Column ${i + 1}`
      if (column.links.length > limits.footer_links) {
        problems.push(`“${name}” holds at most ${limits.footer_links} links.`)
      }
      column.links.forEach((link, j) => linkProblem(link, `Link ${j + 1} in “${name}”`))
    })
  }
  return problems
}

/** Today's header, as a starting point the firm then edits: every page marked
 *  *in the menu*, in order — so the first edit begins from what visitors see. */
export function menuFromPages(pages: NavigationPage[]): HeaderMenu {
  return {
    items: pages.filter((p) => p.in_nav).map((p) => ({ kind: 'page', page_id: p.id, label: null })),
    cta: null,
  }
}

/** Move one entry of a list — the same shape as `reorderBlocks`. */
export function moveItem<T>(items: T[], from: number, to: number): T[] {
  return reorderBlocks(items, from, to)
}

// -- helpers, beside the client so the screens' rules are testable ------------
//
// The shape `lib/api/firm.ts` set in Wave 1A: pure functions here, not inside a
// component, so they can be tested without rendering anything.

/**
 * Which block a save was refused for, and why.
 *
 * The service puts `block_index` and `block_type` on the refusal precisely so
 * an editor with fourteen blocks can scroll to the right one. Returns `null`
 * when the refusal was not about a block.
 */
export function blockIssue(details: Record<string, unknown> | undefined): {
  index: number
  type: string
  field: string | null
  reason: string | null
} | null {
  if (!details || typeof details.block_index !== 'number') return null
  return {
    index: details.block_index,
    type: String(details.block_type ?? ''),
    field: typeof details.field === 'string' ? details.field.replace(/^props\./, '') : null,
    reason: typeof details.reason === 'string' ? details.reason : null,
  }
}

/** Whether a refusal was the HTML rule rather than an ordinary validation
 *  failure — worth a different sentence, because a firm pasting from Word does
 *  not know it has pasted markup. */
export function isMarkupRefusal(details: Record<string, unknown> | undefined): boolean {
  return details?.reason === 'html_not_allowed'
}

/** The gallery order (FR-I10): the firm's profession first, then the two
 *  neutral templates, then the rest. The server already sorts; this is here for
 *  a screen holding a list from somewhere else. */
/**
 * **The seven the owner put at the top** — C24 item 8, in the owner's own
 * order, which is why this is an array and not a set.
 *
 * It exists twice: here and in `app/domain/site.py: GALLERY_LEAD`, which is
 * the authority and which serves it on `vocabulary.gallery_lead`.
 * `tests/site-vocabulary.test.ts` holds the two to each other — a lead this
 * half applied and the service did not would mean the gallery's first card
 * changed as a firm walked from onboarding to Website, which is the class of
 * defect that fixture was written about.
 */
export const GALLERY_LEAD = [
  'web3-cipher',
  'learning-campus',
  'event-encore',
  'care-haven',
  'corporate-keystone',
  'practice-modern',
  'travel-voyage',
] as const

/**
 * The gallery's order, **client-side** — `GALLERY_LEAD`'s seven first, then
 * the firm's profession, then the neutral ones, then the rest by name.
 *
 * **The service orders the gallery already**, and the Website screen takes
 * that order untouched. This exists for **onboarding**, which re-orders for
 * the profession the firm picked one step earlier and which the profile may
 * not carry yet — so the two must agree, and the vocabulary test is what
 * makes them.
 *
 * **Ordered, never filtered** (FR-I10): every template survives. A cost
 * accountant who prefers the advocate's template is not making a mistake.
 */
export function galleryOrder(templates: Template[], profession: string | null): Template[] {
  const lead = new Map<string, number>(GALLERY_LEAD.map((code, i) => [code, i]))
  const rank = (t: Template) =>
    lead.has(t.code) ? 0 : profession && t.profession === profession ? 1 : t.profession === null ? 2 : 3
  const within = (t: Template) => lead.get(t.code) ?? 0
  return [...templates].sort(
    (a, b) => rank(a) - rank(b) || within(a) - within(b) || a.name.localeCompare(b.name),
  )
}

/** One line for a policy switch's card, from its basis. The citation itself is
 *  on `PolicyDefault.quote` and belongs beside it — this is only the framing. */
export const BASIS_LABELS: Record<PolicyBasis, string> = {
  prohibited: 'Your institute does not permit this',
  conditional: 'Permitted, on a condition ProDesk cannot check',
  not_addressed: 'Your institute’s guidelines do not address this',
  permitted: 'Your institute permits this',
}

/**
 * Whether the preview link is still live, without re-fetching.
 *
 * The service answers this on `state`; this is for a screen that has had the
 * tab open across the window's end. A link whose window has closed resolves to
 * a 404 for the holder, so the screen must not keep offering it — and must not
 * quietly re-arm it either, which is the service's rule (ADR-0024 §4).
 */
export function previewIsLive(preview: PreviewLink | null, now: Date = new Date()): boolean {
  if (preview?.state !== 'live' || !preview.path || !preview.expires_at) return false
  return new Date(preview.expires_at).getTime() > now.getTime()
}

/** Move a block up or down. The order is the list order and the server
 *  renumbers from it (the shape `lib/api/services.ts: reorder` set). */
export function reorderBlocks<T>(items: T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length || from < 0 || from >= items.length) return items
  const next = [...items]
  const moved = next.splice(from, 1)
  next.splice(to, 0, ...moved)
  return next
}

/** A new block of a type, with the schema's own defaults filled in — so the
 *  editor never has to restate what `count: 3` means. */
export function blankBlock(
  schema: BlockSchema,
): { type: BlockType; props: Record<string, unknown> } {
  const props: Record<string, unknown> = {}
  for (const field of schema.fields) {
    if (field.default !== undefined) props[field.name] = field.default
    else if (field.kind === 'rich' && field.required) props[field.name] = EMPTY_RICH_TEXT
    // C21: a list with a floor starts at it (`tabs` two, `marquee` three,
    // `comparison` two columns), so the form shows how many it needs.
    else if (field.kind === 'items') props[field.name] = Array.from({ length: field.min_items ?? 0 }, () => ({}))
    else if (field.kind === 'slugs') props[field.name] = []
  }
  return { type: schema.type, props }
}

// -- the builder's own rules (Wave 4D) ---------------------------------------
//
// Pure functions, testable without rendering anything — the shape `firm.ts`
// set in Wave 1A and the reason `blockIssue` above is here rather than inside a
// component.

/**
 * Where a template's live demo lives — 3D's `/_demo/<code>` on the platform
 * host (FR-I10).
 *
 * **Same origin, so an iframe just works** and no CORS, no token and no second
 * host is involved. The `profession` is the firm's own, and passing it is what
 * makes the demo "sample content for the firm's profession" rather than for the
 * template's: four of the six templates carry a profession and two carry none,
 * and a company secretary opening Classic should not be shown a CA's services.
 */
export function demoHref(code: string, profession: string | null): string {
  const query = profession ? `?profession=${encodeURIComponent(profession)}` : ''
  return `/_demo/${encodeURIComponent(code)}${query}`
}

/**
 * The preview link to put on a firm's clipboard (FR-I11, C6, ADR-0024 §3).
 *
 * `path` is the service's — `/_preview/<token>` — and **the origin is this
 * bundle's own**, because the builder is served from the platform host and so
 * is the preview. That is the whole of it: no bound domain, no second host, no
 * setting, and no platform ask (§9-17 closed).
 *
 * It used to take the firm's bound host and return `null` without one, which
 * is why the builder said *"a preview link needs an address to point at, so it
 * waits for your domain"* — the one promise FR-I11 makes by name is that a
 * preview works **before** the domain is bound, and that was the line that
 * broke it.
 *
 * `origin` is a parameter only so a test can pass one; in the app it is
 * `window.location.origin`.
 */
export function previewUrl(
  path: string | null,
  origin: string = typeof window === 'undefined' ? '' : window.location.origin,
): string | null {
  if (!path) return null
  return `${origin.replace(/\/+$/, '')}${path}`
}

/**
 * How long the link has left, in whole days, or `null` if it is not live.
 *
 * The window **slides** (ADR-0024 §4), so this is not a countdown to anything
 * a working firm will ever meet: the firm editing and the reviewer opening both
 * push it back to the full ninety. It is what the screen says to a firm that
 * has not touched its site in months.
 */
export function previewDaysLeft(
  preview: PreviewLink | null,
  now: Date = new Date(),
): number | null {
  if (!previewIsLive(preview, now)) return null
  const ms = new Date(preview!.expires_at as string).getTime() - now.getTime()
  return Math.max(1, Math.ceil(ms / 86_400_000))
}

/**
 * One row of FR-I12's setup checklist.
 *
 * `done` is **computed from the data**, never stored: a stored flag is a flag
 * that says a firm has bound a domain after the binding lapsed. `href` deep
 * links to the screen that clears the row.
 */
export interface ChecklistRow {
  key: string
  label: string
  hint: string
  href: string
  done: boolean
  /**
   * A **second** screen this row's work is done on — Wave 8H, finding 2.
   *
   * A UAT firm reported *"no option to add website logo and favicon"*. There is
   * one, on Site → Theme, and there always was. It was not missing; it was
   * unreachable from anywhere a firm setting up its website would look, because
   * the checklist row about the site's content points at Pages and nothing on
   * the builder's home said *Theme* at all.
   *
   * So a row may name one more place, drawn beside it. It never ticks the row —
   * `done` is still computed from the row's own data — and it is not a second
   * row, because a firm with no logo has not failed to set its site up.
   */
  also?: { label: string; href: string }
  /** Spec C3. The firm's own word on a row the data has **not** ticked — set
   *  by `withMarks`, and never present on a row whose data ticked it, because
   *  the data outranks a mark. */
  mark?: ChecklistMarkState
  /** Rows a firm cannot clear yet — the wave that fills them has not shipped.
   *  Shown, greyed, with the reason: a checklist that hides what is coming
   *  makes a firm think it is finished when it is not. */
  blocked?: string
}

export interface ChecklistInputs {
  profession: string | null
  /** **`legal_name`, not `display_name`.** No screen writes a display name —
   *  the wizard and Settings both collect the legal name — so a checklist fed
   *  the other column has a first row that can never be ticked (ACCEPTANCE
   *  §3.5). */
  firmName: string | null
  /** **`firm_profile.website_template`, not `prodesk_site.template`.** The
   *  second is `NOT NULL` with a `server_default`, so a firm one second old
   *  arrives with it already set and this row ticks before anybody picks
   *  anything. The first is nullable and records the firm's own act; the two
   *  are kept in step by `domain/site.py: adopt_template` (ACCEPTANCE §3.5). */
  template: string | null
  /** Pages in the draft that carry at least one block. */
  pagesWithContent: number
  serviceCount: number
  pricedServiceCount: number
  domainBound: boolean
  channelsConfigured: boolean
  gatewayConfigured: boolean
  clientCount: number
  caseCount: number
  published: boolean
}

/**
 * FR-I12's nine rows, in the order dukaan §1 puts them — the order a firm
 * actually does them in, not the order the data model would suggest.
 *
 * **The whole list is returned and the screen hides it when every row is
 * done**, rather than this function returning only what is outstanding: a
 * progress count needs the denominator, and a firm that has finished eight of
 * nine should see eight ticks and not one line.
 */
export function checklist(inputs: ChecklistInputs): ChecklistRow[] {
  return [
    {
      key: 'profession',
      label: 'Your profession and firm details',
      hint: 'This chooses your conduct policy defaults and your template.',
      // Wave 12 close: the settings hub has a page for each half of this row.
      href: '/settings/profession',
      done: Boolean(inputs.profession && inputs.firmName),
      also: { label: 'Firm details', href: '/settings/firm' },
    },
    {
      key: 'template',
      label: 'Pick a template',
      hint: 'Eight to choose from; switching keeps everything you have written.',
      href: '/site/templates',
      done: Boolean(inputs.template),
    },
    {
      key: 'content',
      label: 'Write your pages',
      hint: 'Home, about, services and contact are the four that matter — and your logo and browser icon go on in one click.',
      href: '/site/pages',
      done: inputs.pagesWithContent >= 4,
      // w8h, finding 2. The row a firm is on when it wants its logo, pointing
      // at the screen that takes one. See `ChecklistRow.also`.
      also: { label: 'Logo and icon', href: '/site/theme' },
    },
    {
      key: 'services',
      label: 'List your services and their fees',
      hint: 'The site reads the catalog live, so it is never retyped.',
      href: '/services',
      done: inputs.serviceCount > 0 && inputs.pricedServiceCount > 0,
    },
    {
      key: 'domain',
      label: 'Bind your domain',
      hint: 'Your website and your client portal both live on it.',
      // Wave 14 (C13, ADR-0032 §1): the domain is edited under the website.
      href: '/site/domain',
      done: inputs.domainBound,
    },
    {
      key: 'publish',
      label: 'Publish your site',
      hint: 'Nothing is public until you do.',
      href: '/site',
      done: inputs.published,
    },
    {
      key: 'channels',
      label: 'Turn on email and messaging',
      hint: 'Enabled in the platform console; ProDesk reads what you have.',
      href: '/settings/integrations',
      done: inputs.channelsConfigured,
    },
    {
      key: 'client',
      label: 'Add your first client',
      hint: 'A client record is what everything else hangs from.',
      href: '/clients',
      done: inputs.clientCount > 0,
    },
    {
      key: 'case',
      label: 'Open your first case',
      hint: 'On one of your services, so the workflow drives it.',
      href: '/cases',
      done: inputs.caseCount > 0,
    },
  ]
}

/** Spec C3. What a firm may say about a row the data has not ticked. */
export type ChecklistMarkState = 'done' | 'skipped'

export interface ChecklistMark {
  row_key: string
  state: ChecklistMarkState
  actor_user_id: string | null
  marked_at: string | null
}

/**
 * The firm's marks laid over the computed rows (spec C3).
 *
 * **The data outranks the mark.** A row the data ticked keeps `done: true` and
 * carries no mark, so a stale "skipped" on a domain the firm later bound never
 * shows as skipped. A mark on a key this list does not draw is ignored.
 */
export function withMarks(rows: ChecklistRow[], marks: ChecklistMark[]): ChecklistRow[] {
  const byKey = new Map(marks.map((mark) => [mark.row_key, mark.state]))
  return rows.map((row) => {
    const mark = byKey.get(row.key)
    return row.done || !mark ? row : { ...row, mark }
  })
}

/**
 * The progress line and the hide condition, from one place (spec C3):
 * *"n of 9 done · m skipped"*, where `done` counts a row ticked by its data or
 * marked done, and the panel is `complete` once nothing is left open.
 */
export function checklistProgress(rows: ChecklistRow[]): {
  done: number
  skipped: number
  total: number
  complete: boolean
} {
  const done = rows.filter((row) => row.done || row.mark === 'done').length
  const skipped = rows.filter((row) => !row.done && row.mark === 'skipped').length
  return { done, skipped, total: rows.length, complete: done + skipped === rows.length }
}

/**
 * Which block types a firm may add to a page, given what is already on it.
 *
 * `services`, `contact` and `blog_list` resolve from the catalog, the firm's
 * details and the posts at render, so a second one on the same page renders the
 * same thing twice. `hero` is the band at the top and there is one top. The
 * rest repeat freely — a page with four `rich_text` blocks is a page.
 *
 * **`page_hero` joined them with FR-I14** (C5), on the library's own argument
 * for why it carries no image: *"the full-height band with a picture is `hero`,
 * and two heroes on one page is a page with two beginnings."* That is a
 * statement about a second opening band, not about the picture.
 *
 * It says nothing about `hero` and `page_hero` **together** — a firm may
 * legitimately want either, and the spec does not forbid both, so neither does
 * this.
 */
const SINGLETON_BLOCKS: BlockType[] = ['hero', 'page_hero', 'services', 'contact', 'blog_list']

export function addableBlocks(schemas: BlockSchema[], present: BlockType[]): BlockSchema[] {
  const taken = new Set(present)
  return schemas.filter((s) => !(SINGLETON_BLOCKS.includes(s.type) && taken.has(s.type)))
}

/**
 * Switching template keeps every block (ADR-0013 §3, FR-I10) — this is the
 * sentence a firm is shown before it does, and it is only reassuring if it is
 * true, so it counts the actual blocks.
 */
export function switchTemplateNote(blockCount: number, from: Template | null, to: Template): string {
  if (from && from.code === to.code) return `${to.name} is the template you are using.`
  const kept =
    blockCount === 0
      ? 'You have not written anything yet, so there is nothing to keep.'
      : `Everything you have written stays — ${blockCount} block${blockCount === 1 ? '' : 's'} across your pages. Only the look changes.`
  return `${kept} Your colours and font reset to ${to.name}'s, and you can tune them afterwards.`
}

/**
 * FR-I4 and T-4.10, as one sentence per switch for the policy screen.
 *
 * `show_prices_public` is the only one of ADR-0014's five with a block to hide
 * today, and the service enforces it **by omission** — the price is absent from
 * the render payload, not hidden in the markup, so it is not in the page source
 * either. The other four have nothing to hide among ADR-0013's nine block
 * types (spec §9-18, open) and the screen must say so rather than implying an
 * enforcement that does not happen. Telling a firm a switch protects it when
 * nothing reads it is worse than telling it the switch is advisory.
 */
export const SWITCH_EFFECT: Record<string, { effect: string; enforced: boolean }> = {
  show_prices_public: {
    effect:
      'Off, your fees are left out of the public site entirely — not hidden, absent. Clients always see them in the portal after signing in.',
    enforced: true,
  },
  // **Enforced since FR-I14** (2026-09-21, C5 / ADR-0022 §6). Both of these
  // said "advisory — there is nothing to hide" for as long as that was true,
  // and the section library gave them something to hide the day it shipped. A
  // screen that still called them advisory would be the false assurance about
  // a regulator that `routes/Site/Policy.tsx` is written to avoid, pointing
  // the other way.
  show_client_names: {
    effect:
      'Off, the Client names and Client stories sections are left out of your site entirely — not hidden, absent. Your pages keep them; nobody visiting sees them.',
    enforced: true,
  },
  show_testimonials: {
    effect:
      'Off, the What clients say section is left out of your site entirely — not hidden, absent. Your quotes are kept and nothing is deleted.',
    enforced: true,
  },
  show_comparative_claims: {
    effect:
      'Advisory. ProDesk cannot read what you write and judge whether it compares you to another firm. It is why no section carries a comparison field at all; the words are yours.',
    enforced: false,
  },
  allow_third_party_ads: {
    effect:
      'Advisory, and off in the product regardless: nothing third-party runs on your site — no scripts, no pixels, no ad slots (ADR-0013 §4).',
    enforced: false,
  },
}
